import React from "react";
import {
  Box,
  Button,
  IconButton,
  Modal,
  TextField,
  Typography,
  useTheme
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import CloseIcon from "@mui/icons-material/Close";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { useUpdateCategory } from "../../hooks/api/useCategory";
import {
  BRAND_COLOR,
  BRAND_HOVER,
  useKivunovaTranslation
} from "@kivunova/kivufrontendcommon";
import { Category, CategoryFormData } from "./types/categoryTypes";
import useMediaQuery from "@mui/material/useMediaQuery";

const getSchema = (t: (key: string, fallback: string) => string) =>
  Yup.object().shape({
    name: Yup.string().required(
      t("category-name-required", "Category name is required")
    ),
    description: Yup.string().optional(),
    parentId: Yup.string().optional().nullable()
  }) as Yup.ObjectSchema<CategoryFormData>;

interface EditCategoryModalProps {
  open: boolean;
  onClose: () => void;
  category: Category;
  onSuccess?: () => void | Promise<void>;
}

const EditCategoryModal: React.FC<EditCategoryModalProps> = ({
  open,
  onClose,
  category,
  onSuccess
}) => {
  const theme = useTheme();
  const matchMD = useMediaQuery(theme.breakpoints.down("md"));
  const updateCategory = useUpdateCategory();
  const { t } = useKivunovaTranslation();

  const schema = getSchema(t);

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting }
  } = useForm<CategoryFormData>({
    resolver: yupResolver(schema),
    mode: "onChange",
    defaultValues: {
      name: category.name,
      description: category.description,
      parentId: category.parentId ?? null,
      companyId: category.companyId,
      createdBy: category.createdBy
    }
  });

  const onSubmit: SubmitHandler<CategoryFormData> = async (data) => {
    try {
      await updateCategory.mutateAsync({
        id: category.id,
        name: data.name,
        description: data.description ?? undefined,
        parentId: data.parentId ?? undefined,
        status: category.status
      });
      await onSuccess?.();
      onClose();
    } catch (error) {
      console.error("❌ Failed to update category:", error);
    }
  };

  const isLoading = isSubmitting || updateCategory.isPending;

  return (
    <Modal open={open} onClose={onClose}>
      <Box className="modal-styles">
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: matchMD ? 2 : 3,
            pb: 2,
            borderBottom: "1px solid",
            borderColor: "divider",
            flexShrink: 0
          }}
        >
          <Typography
            variant={matchMD ? "h6" : "h5"}
            fontWeight="bold"
            color="textPrimary"
          >
            {t("edit-category", "Edit Category")}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Scrollable Content Area */}
        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            p: matchMD ? 2 : 3,
            pt: 2,
            display: "flex",
            flexDirection: "column",
            "&::-webkit-scrollbar": {
              width: "8px"
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: "#f1f1f1",
              borderRadius: "10px"
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "#888",
              borderRadius: "10px",
              "&:hover": {
                backgroundColor: "#555"
              }
            }
          }}
        >
          {/* Form - Vertical Layout */}
          <Box
            component="form"
            onSubmit={handleSubmit(onSubmit)}
            noValidate
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: matchMD ? 1.5 : 2
            }}
          >
            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  size={matchMD ? "small" : "medium"}
                  label={t("category-name", "Category Name")}
                  placeholder={t(
                    "category-name-placeholder",
                    "Enter category name"
                  )}
                  error={!!errors.name}
                  helperText={errors.name?.message}
                  disabled={isLoading}
                />
              )}
            />

            <Controller
              name="description"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  size={matchMD ? "small" : "medium"}
                  label={t("description", "Description")}
                  placeholder={t(
                    "category-description-placeholder",
                    "Enter category description"
                  )}
                  multiline
                  rows={3}
                  error={!!errors.description}
                  helperText={errors.description?.message}
                  disabled={isLoading}
                />
              )}
            />

            <Controller
              name="parentId"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  size={matchMD ? "small" : "medium"}
                  label={t("parent-category-id", "Parent Category ID")}
                  placeholder={t(
                    "parent-category-id-placeholder",
                    "Enter parent category ID (optional)"
                  )}
                  error={!!errors.parentId}
                  helperText={errors.parentId?.message}
                  disabled={isLoading}
                />
              )}
            />
          </Box>
        </Box>

        {/* Fixed Action Buttons */}
        <Box
          sx={{
            display: "flex",
            flexDirection: matchMD ? "column" : "row",
            gap: matchMD ? 1 : 2,
            p: matchMD ? 2 : 3,
            pt: matchMD ? 1.5 : 2,
            borderTop: "1px solid",
            borderColor: "divider",
            flexShrink: 0,
            backgroundColor: "background.paper"
          }}
        >
          <LoadingButton
            type="submit"
            variant="contained"
            loading={isLoading}
            onClick={handleSubmit(onSubmit)}
            fullWidth
            size={matchMD ? "medium" : "large"}
            sx={{
              textTransform: "none",
              borderRadius: 2,
              backgroundColor: BRAND_COLOR,
              "&:hover": { backgroundColor: BRAND_HOVER }
            }}
          >
            {t("save-changes", "Save Changes")}
          </LoadingButton>

          <Button
            type="button"
            variant="outlined"
            onClick={onClose}
            disabled={isLoading}
            fullWidth
            size={matchMD ? "medium" : "large"}
            sx={{
              textTransform: "none",
              borderRadius: 2,
              backgroundColor: "white",
              color: BRAND_COLOR,
              border: `1px solid ${BRAND_COLOR}`,
              "&:hover": {
                backgroundColor: BRAND_HOVER,
                color: "white",
                borderColor: BRAND_HOVER
              }
            }}
          >
            {t("cancel", "Cancel")}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default EditCategoryModal;
